// Страница упражнения: техника, рекорды, график рабочего веса и расчётного 1ПМ, прошлая тренировка.

import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { TimeSeriesChart, type ChartSeries } from '@/components/charts/TimeSeriesChart'
import { PageHeader } from '@/components/layout'
import { Badge, Button, Card, EmptyState, ErrorText, PageSpinner, Stat } from '@/components/ui'
import { formatDate, formatDuration, formatNumber } from '@/lib/format'
import {
  trainingApi,
  trainingKeys,
  useExercise,
  useInvalidateTraining,
  useMuscleLabels,
  type Exercise,
  type RecordValue,
  type Records,
} from '@/pages/workouts/api'
import { EQUIPMENT_LABELS, TRACKING_LABELS, formatSet, summarizeSets } from '@/pages/workouts/logic'

import { ExerciseForm } from './ExerciseForm'
import { ExerciseTechnique } from './ExerciseInfo'

type RecordTile = { key: keyof Omit<Records, 'exercise_id'>; label: string; format: (r: RecordValue) => string; unit?: string }

const TILES: RecordTile[] = [
  { key: 'weight', label: 'Лучший вес', format: (r) => formatNumber(r.value, { digits: 2 }), unit: 'кг' },
  { key: 'e1rm', label: 'Расчётный 1ПМ', format: (r) => formatNumber(r.value, { digits: 1 }), unit: 'кг' },
  { key: 'volume', label: 'Объём подхода', format: (r) => formatNumber(r.value, { digits: 1 }), unit: 'кг' },
  { key: 'reps', label: 'Больше всего повторов', format: (r) => formatNumber(r.value, { digits: 0 }) },
  { key: 'duration', label: 'Дольше всего', format: (r) => formatDuration(r.value) },
  { key: 'distance', label: 'Дальше всего', format: (r) => formatNumber(r.value, { digits: 0 }), unit: 'м' },
]

function RecordsCard({ exercise }: { exercise: Exercise }) {
  const records = useQuery({ queryKey: trainingKeys.records(exercise.id), queryFn: () => trainingApi.records(exercise.id) })
  if (records.isPending) return <Card title="Рекорды">…</Card>
  if (records.error) return <ErrorText error={records.error} />
  const tiles = TILES.filter((t) => records.data[t.key])
  return (
    <Card title="Рекорды" subtitle="Только выполненные рабочие подходы; разминка не считается.">
      {tiles.length === 0 ? (
        <p className="text-sm text-muted">Рекордов пока нет — они появятся после первой тренировки.</p>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {tiles.map((tile) => {
            const r = records.data[tile.key]!
            const detail = r.weight_kg && r.reps ? formatSet({ ...r, duration_s: null, distance_m: null }, exercise.tracking) : null
            return (
              <Stat
                key={tile.key}
                label={tile.label}
                value={tile.format(r)}
                unit={tile.unit}
                hint={
                  <Link to={`/workouts/${r.session_id}`} className="hover:text-accent">
                    {formatDate(r.achieved_at)}
                    {detail ? ` · ${detail}` : ''}
                  </Link>
                }
              />
            )
          })}
        </div>
      )}
    </Card>
  )
}

function ProgressCard({ exercise }: { exercise: Exercise }) {
  const progress = useQuery({ queryKey: trainingKeys.progress(exercise.id), queryFn: () => trainingApi.progress(exercise.id) })
  const points = progress.data ?? []
  const series: ChartSeries[] =
    exercise.tracking === 'time'
      ? [{ key: 'duration', label: 'Время', unit: 'с', digits: 0, points: points.map((p) => ({ x: p.started_at, y: p.duration_s })) }]
      : exercise.tracking === 'distance'
        ? [{ key: 'distance', label: 'Дистанция', unit: 'м', digits: 0, points: points.map((p) => ({ x: p.started_at, y: p.distance_m })) }]
        : points.some((p) => p.weight_kg)
          ? [
              { key: 'weight', label: 'Рабочий вес', unit: 'кг', points: points.map((p) => ({ x: p.started_at, y: p.weight_kg })) },
              { key: 'e1rm', label: 'Расчётный 1ПМ', unit: 'кг', axis: 'left', points: points.map((p) => ({ x: p.started_at, y: p.e1rm })) },
            ]
          : [{ key: 'reps', label: 'Повторы', digits: 0, points: points.map((p) => ({ x: p.started_at, y: p.reps })) }]
  return (
    <Card title="Прогресс" subtitle="Точка — лучший рабочий подход тренировки.">
      {progress.error ? <ErrorText error={progress.error} /> : <TimeSeriesChart series={series} defaultPeriod="all" emptyText="Пока нет тренировок с этим упражнением" />}
    </Card>
  )
}

function LastCard({ exercise }: { exercise: Exercise }) {
  const last = useQuery({ queryKey: trainingKeys.last(exercise.id), queryFn: () => trainingApi.last(exercise.id) })
  const sets = last.data ?? []
  const working = sets.filter((s) => !s.is_warmup)
  return (
    <Card
      title="В прошлый раз"
      actions={
        sets[0] ? (
          <Link to={`/workouts/${sets[0].session_id}`} className="hit-area text-sm text-muted hover:text-accent">
            Открыть тренировку
          </Link>
        ) : null
      }
    >
      {sets.length === 0 ? (
        <p className="text-sm text-muted">Ещё не выполнялось.</p>
      ) : (
        <p className="text-sm tabular-nums text-fg">
          {summarizeSets(working, exercise.tracking)}
          {sets[0]?.completed_at ? <span className="text-muted"> · {formatDate(sets[0].completed_at)}</span> : null}
        </p>
      )}
    </Card>
  )
}

export function ExercisePage() {
  const { id = '' } = useParams()
  const exercise = useExercise(id)
  const muscleLabel = useMuscleLabels()
  const invalidate = useInvalidateTraining()
  const [editing, setEditing] = useState(false)
  const archive = useMutation({
    mutationFn: (archived: boolean) => trainingApi.updateExercise(id, { archived }),
    onSuccess: invalidate,
  })

  if (exercise.isPending) return <PageSpinner />
  if (exercise.error) {
    return (
      <>
        <PageHeader title="Упражнение" back={{ to: '/exercises', label: 'Упражнения' }} />
        <Card>
          <EmptyState icon="alert" title="Не удалось открыть упражнение" description={<ErrorText error={exercise.error} />} />
        </Card>
      </>
    )
  }
  const ex = exercise.data
  return (
    <>
      <PageHeader
        title={ex.name}
        back={{ to: '/exercises', label: 'Упражнения' }}
        description={
          <>
            {muscleLabel(ex.primary_muscle)}
            {ex.secondary_muscles.length ? `, ${ex.secondary_muscles.map(muscleLabel).join(', ').toLowerCase()}` : ''} ·{' '}
            {EQUIPMENT_LABELS[ex.equipment]} · {TRACKING_LABELS[ex.tracking]}
          </>
        }
        actions={
          <>
            {ex.archived ? <Badge tone="warning">в архиве</Badge> : null}
            <Button onClick={() => setEditing(true)}>Изменить</Button>
            <Button loading={archive.isPending} onClick={() => archive.mutate(!ex.archived)}>
              {ex.archived ? 'Вернуть из архива' : 'В архив'}
            </Button>
          </>
        }
      />
      {archive.error ? <ErrorText error={archive.error} className="mb-3" /> : null}
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="grid min-w-0 gap-4">
          <RecordsCard exercise={ex} />
          <ProgressCard exercise={ex} />
        </div>
        <div className="grid min-w-0 content-start gap-4">
          <Card title="Техника">
            <ExerciseTechnique exercise={ex} showMeta={false} />
          </Card>
          <LastCard exercise={ex} />
          <Card title="Garmin">
            <p className="font-mono text-xs text-muted">
              {ex.garmin_category ?? '—'} / {ex.garmin_name ?? '—'}
            </p>
            {!ex.garmin_name ? (
              <p className="mt-2 text-sm text-muted">Без точного сопоставления на часах будет показано общее упражнение.</p>
            ) : null}
          </Card>
          <Link to={`/workouts?exercise_id=${ex.id}`} className="hit-area inline-flex min-h-11 items-center text-sm text-muted hover:text-accent">
            Все тренировки с этим упражнением →
          </Link>
        </div>
      </div>
      {editing ? <ExerciseForm open onClose={() => setEditing(false)} exercise={ex} /> : null}
    </>
  )
}
